/**
 * Where the admin tokens are kept. "Remember me" keeps them in localStorage,
 * otherwise they only live for the browser session (sessionStorage).
 */
const TOKEN_KEY = "token";
const REFRESH_KEY = "refresh_token";

const read = (key: string) => localStorage.getItem(key) ?? sessionStorage.getItem(key);

export function getToken(): string | null {
  return read(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return read(REFRESH_KEY);
}

/** Saves a new token pair; without `remember` the storage of the current session is reused. */
export function setTokens(accessToken: string, refreshToken: string, remember?: boolean) {
  const keep = remember ?? localStorage.getItem(REFRESH_KEY) !== null;
  clearToken();
  const storage = keep ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, accessToken);
  storage.setItem(REFRESH_KEY, refreshToken);
}

export function clearToken() {
  [TOKEN_KEY, REFRESH_KEY].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export function isLoggedIn(): boolean {
  return !!getToken() || !!getRefreshToken();
}

export interface CurrentAdmin {
  id: number;
  userName: string;
  email: string;
}

/**
 * Who is logged in, read from the access token (display only — the server checks the token itself).
 * Claim names may be the short JWT names or the long ClaimTypes URIs.
 */
export function getCurrentAdmin(): CurrentAdmin | null {
  const token = getToken();
  if (!token) return null;
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(part), (c) => c.charCodeAt(0));
    const claims: Record<string, unknown> = JSON.parse(new TextDecoder().decode(bytes));
    const claim = (...names: string[]) => {
      const key = Object.keys(claims).find((k) => names.some((n) => k === n || k.endsWith(`/${n}`)));
      return key ? String(claims[key]) : "";
    };
    return {
      id: Number(claim("nameid", "nameidentifier")),
      userName: claim("unique_name", "name"),
      email: claim("email", "emailaddress"),
    };
  } catch {
    return null;
  }
}
